import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';

import useAuthentication, {
  SignInMethod,
  AuthErrorCode,
} from '../../../../hooks/useAuthentication';

import { SuccessMessage, ErrorMessage, ActionButton } from '../../styles';

import ForgotPasswordContainer from './styles';

const ForgotPassword = ({
  email,
}) => {
  const {
    getSignInMethod,
    sendPasswordResetEmail,
    createAnonymousAccountWithEmail,
  } = useAuthentication();
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const onForgotPasswordClick = async () => {
    try {
      const authMethod = await getSignInMethod(email);

      if (authMethod === SignInMethod.NO_ACCOUNT) {
        try {
          const user = await createAnonymousAccountWithEmail(email);
          await user.delete();
          setSuccessMessage(null);
          setError({ message: 'No such user.' });
        } catch (err) {
          if (err.code === AuthErrorCode.EMAIL_ALREADY_IN_USE) {
            // Account appeared between getSignInMethod and here (race). It's
            // registered, so send a reset email like the main path below.
            await sendPasswordResetEmail(email);
            setSuccessMessage(`Password reset email sent to ${email}`);
            setError(null);
          } else {
            Sentry.captureException(err, {
              extra: { errorMessage: err.message, errorCode: err.code, email },
            });
          }
        }
        return;
      }

      // Both EMAIL_PASSWORD and EMAIL_LINK accounts get a reset link. For a
      // passwordless (email-link) account, completing the reset adds a password
      // provider, upgrading the account.
      await sendPasswordResetEmail(email);
      setSuccessMessage(`Password reset email sent to ${email}`);
      setError(null);
    } catch (e) {
      setSuccessMessage(null);
      setError(e);
    }
  };

  return (
    <ForgotPasswordContainer>
      <ActionButton onClick={onForgotPasswordClick}>Forgot Password?</ActionButton>
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
    </ForgotPasswordContainer>
  );
};

ForgotPassword.propTypes = {
  email: PropTypes.string.isRequired,
};

export default ForgotPassword;
